

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Sparkles,
  CheckCircle,
  Star,
} from "lucide-react";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { featuresApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface Feature {
  id: number;
  title: string;
  description: string;
  icon: string | null;
  category: string | null;
  is_active: boolean;
  is_selected: boolean;
  sort_order: number;
  created_at?: string;
}

const ICON_OPTIONS = [
  { value: 'venue', label: 'Venue' },
  { value: 'catering', label: 'Catering' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'photography', label: 'Photography' },
  { value: 'decor', label: 'Decor' },
  { value: 'bar', label: 'Bar' },
  { value: 'planning', label: 'Planning' },
  { value: 'music', label: 'Music' },
  { value: 'star', label: 'Star' },
];

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function FeatureForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<Feature>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [icon, setIcon] = useState(initialData?.icon || 'star');
  const [category, setCategory] = useState(initialData?.category || '');
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [isSelected, setIsSelected] = useState(initialData?.is_selected ?? false);
  const [sortOrder, setSortOrder] = useState(initialData?.sort_order || 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      icon,
      category,
      is_active: isActive,
      is_selected: isSelected,
      sort_order: sortOrder,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Title *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          placeholder="Enter feature title"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none min-h-[100px]"
          placeholder="Enter feature description"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Icon</label>
          <select
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          >
            {ICON_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Category</label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            placeholder="e.g., venue, catering"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Sort Order</label>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Active</label>
          <button
            type="button"
            onClick={() => setIsActive(!isActive)}
            className={`w-full px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              isActive ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {isActive ? "Yes" : "No"}
          </button>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Featured</label>
          <button
            type="button"
            onClick={() => setIsSelected(!isSelected)}
            className={`w-full px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              isSelected ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {isSelected ? "Yes" : "No"}
          </button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Feature
        </button>
      </div>
    </form>
  );
}

export default function FeaturesManager() {
  const { showToast } = useToast();

  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingFeature, setDeletingFeature] = useState<Feature | null>(null);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await featuresApi.list();
      const data = response.data?.data || [];
      setFeatures(data);
    } catch (error) {
      showToast('Failed to load features', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeatures();
  }, []);

  const handleCreate = async (data: any) => {
    try {
      await featuresApi.create(data);
      showToast('Feature created successfully', 'success');
      setIsModalOpen(false);
      loadFeatures();
    } catch (error) {
      showToast('Failed to create feature', 'error');
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingFeature) return;
    try {
      await featuresApi.update(editingFeature.id, data);
      showToast('Feature updated successfully', 'success');
      setIsModalOpen(false);
      setEditingFeature(null);
      loadFeatures();
    } catch (error) {
      showToast('Failed to update feature', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deletingFeature) return;
    try {
      await featuresApi.delete(deletingFeature.id);
      showToast('Feature deleted successfully', 'success');
      setDeleteModalOpen(false);
      setDeletingFeature(null);
      loadFeatures();
    } catch (error) {
      showToast('Failed to delete feature', 'error');
    }
  };

  const handleToggleActive = async (feature: Feature) => {
    try {
      await featuresApi.update(feature.id, { is_active: !feature.is_active });
      showToast('Status updated successfully', 'success');
      loadFeatures();
    } catch (error) {
      showToast('Failed to update status', 'error');
    }
  };

  const handleToggleSelected = async (feature: Feature) => {
    try {
      await featuresApi.update(feature.id, { is_selected: !feature.is_selected });
      showToast('Featured status updated', 'success');
      loadFeatures();
    } catch (error) {
      showToast('Failed to update featured status', 'error');
    }
  };

  const filteredFeatures = useMemo(() => {
    if (!searchQuery) return features;
    const q = searchQuery.toLowerCase();
    return features.filter(f =>
      f.title.toLowerCase().includes(q) ||
      f.description.toLowerCase().includes(q)
    );
  }, [features, searchQuery]);

  const stats = useMemo(
    () => ({
      total: features.length,
      active: features.filter(f => f.is_active).length,
      selected: features.filter(f => f.is_selected).length,
    }),
    [features]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Active" value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <StatBadge label="Featured" value={stats.selected} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search features..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditingFeature(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Feature
        </button>
      </div>

      {/* Features List */}
      {filteredFeatures.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Sparkles className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No features found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFeatures.map((feature) => (
            <div
              key={feature.id}
              className="bg-white rounded-xl border border-stone-200 p-3 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-stone-800 text-sm">{feature.title}</p>
                    {feature.category && (
                      <span className="text-xs px-2 py-0.5 rounded bg-stone-100 text-stone-600">
                        {feature.category}
                      </span>
                    )}
                  </div>
                  <p className="text-stone-600 text-xs line-clamp-1">{feature.description}</p>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(feature)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      feature.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-stone-400 bg-stone-50'
                    }`}
                    title={feature.is_active ? 'Active' : 'Inactive'}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleToggleSelected(feature)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      feature.is_selected ? 'text-amber-600 bg-amber-50' : 'text-stone-400 bg-stone-50'
                    }`}
                    title={feature.is_selected ? 'Featured' : 'Not Featured'}
                  >
                    <Star className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setEditingFeature(feature);
                      setIsModalOpen(true);
                    }}
                    className="p-1.5 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setDeletingFeature(feature);
                      setDeleteModalOpen(true);
                    }}
                    className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingFeature(null);
        }}
        title={editingFeature ? 'Edit Feature' : 'Add New Feature'}
        size="md"
      >
        <FeatureForm
          initialData={editingFeature || undefined}
          onSubmit={editingFeature ? handleUpdate : handleCreate}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingFeature(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingFeature(null);
        }}
        onConfirm={handleDelete}
        title="Delete Feature"
        message="Are you sure you want to delete this feature? This action cannot be undone."
        itemName={deletingFeature?.title}
      />
    </div>
  );
}
